import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Select, {components} from 'react-select';
import {ChevronDown, XCircle, Building2, User} from 'lucide-react';
import {useAuth} from '../../../../contexts/UserContext';
import api from '../../../../services/api';
import {comum} from '../../../../styles/Themes/light';
import {EstadosBrasileiros} from '../../../Data/StatesData';
import { getFusoHorarioPorEstado, getFusoHorarioPorComarca } from '../../../../utility/timezoneUtils';
import { formatCurrency } from '../../../../utility/currencyUtils';
import DateTimePicker from '../../../ListaDemandas/FiltroListaDemandas/DateTimePicker';
import {FormSectionTitle} from '../../styles';
import {IDadosBasicosNovaDemanda, InformacoesBasicasProps} from '../types';
import {
  Container,
  createTimezoneSelectStyles,
  DatePickerContainer,
  DateTimeContainer,
  ErrorMessage,
  FieldContainer,
  FormGrid,
  Input,
  Label,
  selectComponents,
  selectTheme,
  ThreeColumnRow,
  TimezoneFieldContainer,
  VincularAContainer,
  VincularALabelAndTabWrapper,
  VincularALabel,
  VincularAFormControl,
  TipoParteContainer,
  TipoParteButton,
  VincularASolicitanteWrapper,
  ClienteFieldsContainer,
  SolicitanteSelectButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  CloseButton,
  SearchContainer,
  OfficeList,
  OfficeItem,
  NoResultsMessage,
} from './styles';

const createSelectStyles = (hasError: boolean) => ({
  control: (provided: Record<string, unknown>, state: any) => ({
    ...provided,
    height: 48,
    boxShadow: "none",
    cursor: 'pointer',
    border: hasError 
      ? `1px solid ${comum.colors.primaryRed400}` 
      : '1px solid var(--ce2e8f0)',
    '&:hover': {
      border: hasError 
        ? `1px solid ${comum.colors.primaryRed400}` 
        : `1px solid ${comum.colors.interfaceTextGray}`,
    },
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c353a40)',
    fontSize: '16px',
    fontWeight: '400',
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c353a40)',
    fontSize: '16px',
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c5a646e)',
    fontSize: '16px',
    fontWeight: '500',
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 3,
  }),
  menuPortal: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 3,
  }),
});

// Interfaces para os componentes internos
interface EstadoSelectProps {
  value: string;
  onChange: (estado: string) => void;
  error?: boolean;
  placeholder?: string;
}

interface ComarcaSelectProps {
  estado: string;
  value: string;
  onChange: (comarca: string) => void;
  error?: boolean;
  placeholder?: string;
  setComarca: (comarca: string) => void;
  onComarcaSelected?: (estado: string) => void;
  handleInputChange?: (campo: string, valor: any) => void;
}

interface ComarcaOption {
  value: string;
  label: string;
}

interface EscritorioSolicitante {
  id_escritorio_solicitante: number;
  nome: string;
  codigo?: number;
}

// Componente EstadoSelect otimizado
const EstadoSelect: React.FC<EstadoSelectProps> = React.memo(({
  value,
  onChange,
  error = false,
  placeholder = "Selecionar"
}) => {
  const selectedOption = useMemo(() => 
    EstadosBrasileiros.find(estado => estado.value === value), 
    [value]
  );

  const handleChange = useCallback((selected: any) => {
    onChange(selected?.value || '');
  }, [onChange]);

  return (
    <FieldContainer>
      <Label required>Estado</Label>
      <Select
        name="estados"
        styles={createSelectStyles(error)}
        components={selectComponents}
        theme={selectTheme}
        placeholder={placeholder}
        value={selectedOption}
        onChange={handleChange}
        options={EstadosBrasileiros}
      />
    </FieldContainer>
  );
});

// Componente ComarcaSelect otimizado
const ComarcaSelect: React.FC<ComarcaSelectProps> = React.memo(({
  estado,
  value,
  onChange,
  setComarca,
  error = false,
  placeholder = "Selecionar",
  onComarcaSelected,
  handleInputChange
}) => {
  const [comarcas, setComarcas] = useState<ComarcaOption[]>([]);
  const [loading, setLoading] = useState(false);

  const getComarcas = useCallback(async (uf: string) => {
    try {
      setLoading(true);
      const response = await api.get(`comarca?uf=${uf}`);
      
      if (response.data.status !== 'success') {
        throw new Error();
      }

      const comarcaArray: ComarcaOption[] = response.data.result.map((row: any) => ({
        label: row.cidade,
        value: row.id_comarca
      }));
      
      setComarcas(comarcaArray);
    } catch (error) {
      console.error('Erro ao buscar comarcas:', error);
      setComarcas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setComarcas([]);
    
    if (estado) {
      getComarcas(estado);
    }
  }, [estado, getComarcas]);

  const selectedOption = useMemo(() => {
    if (!value || comarcas.length === 0) return null;
    return comarcas.find(comarca => comarca.value === value) || null;
  }, [comarcas, value]);

  const handleChange = useCallback(async (selected: any) => {
    onChange(selected?.value || '');
    setComarca(estado + ' - ' + selected?.label || '');
    if (onComarcaSelected && selected) {
      onComarcaSelected(estado);
    }
    
    if (selected?.value && handleInputChange) {
      try {
        const response = await api.get(`comarca/${selected.value}`);
        if (response.data && response.data.status === 'success' && response.data.result) {
          const comarca = response.data.result;
          const fusoDetectado = getFusoHorarioPorComarca(comarca);
          handleInputChange('data_tz', fusoDetectado);
        }
      } catch (error) {
        console.error('Erro ao buscar fuso horário da comarca:', error);
        const fusoPadrao = getFusoHorarioPorEstado(estado);
        handleInputChange('data_tz', fusoPadrao);
      }
    }
  }, [onChange, estado, onComarcaSelected, setComarca, handleInputChange]);

  return (
    <FieldContainer>
      <Label required>Comarca</Label>
      <Select
        styles={createSelectStyles(error)}
        components={selectComponents}
        theme={selectTheme}
        name='id_comarca'
        placeholder={loading ? "Carregando..." : placeholder}
        value={selectedOption}
        options={comarcas}
        onChange={handleChange}
        isDisabled={!estado || loading}
      />
    </FieldContainer>
  );
});

// Componente SolicitanteSelector para seleção de escritório solicitante
interface SolicitanteSelectorProps {
  value?: number;
  onChange: (id: number | undefined) => void;
  error?: boolean;
  inline?: boolean;
  required?: boolean;
  errorMessage?: string;
}

const SolicitanteSelector: React.FC<SolicitanteSelectorProps> = React.memo(({
  value,
  onChange,
  error = false,
  inline = false,
  required = false,
  errorMessage
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [escritorios, setEscritorios] = useState<EscritorioSolicitante[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEscritorio, setSelectedEscritorio] = useState<EscritorioSolicitante | null>(null);

  const carregarEscritorios = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/escritorio/ativos');
      if (response.data.status === 'success') {
        setEscritorios(response.data.result);
      }
    } catch (error) {
      console.error('Erro ao carregar escritórios:', error);
      setEscritorios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      carregarEscritorios();
    }
  }, [isModalOpen, carregarEscritorios]);

  useEffect(() => {
    if (value && escritorios.length > 0) {
      const escritorio = escritorios.find(e => e.id_escritorio_solicitante === value);
      if (escritorio) {
        setSelectedEscritorio(escritorio);
      }
    } else if (!value) {
      setSelectedEscritorio(null);
    }
  }, [value, escritorios]);

  const filteredEscritorios = useMemo(() => 
    escritorios.filter(escritorio =>
      escritorio.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (escritorio.codigo && escritorio.codigo.toString().includes(searchTerm))
    ),
    [escritorios, searchTerm]
  );

  const handleSelectEscritorio = useCallback((escritorio: EscritorioSolicitante) => {
    setSelectedEscritorio(escritorio);
    onChange(escritorio.id_escritorio_solicitante);
    setIsModalOpen(false);
    setSearchTerm('');
  }, [onChange]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSearchTerm('');
  }, []);

  return (
    <>
      {inline ? (
        <>
          <SolicitanteSelectButton
            type="button"
            hasError={error}
            hasValue={!!selectedEscritorio}
            onClick={openModal}
          >
            {selectedEscritorio ? (
              <span className="value">
                {selectedEscritorio.nome} - ES{selectedEscritorio.id_escritorio_solicitante}
              </span>
            ) : (
              <span className="placeholder">Selecionar Solicitante</span>
            )}
            <ChevronDown size={16} color={comum.colors.interfaceText300} />
          </SolicitanteSelectButton>
          {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </>
      ) : (
        <FieldContainer>
          <Label required={required}>Solicitante</Label>
          <SolicitanteSelectButton
            type="button"
            hasError={error}
            hasValue={!!selectedEscritorio}
            onClick={openModal}
          >
            {selectedEscritorio ? (
              <span className="value">
                {selectedEscritorio.nome} - ES{selectedEscritorio.id_escritorio_solicitante}
              </span>
            ) : (
              <span className="placeholder">Selecionar Solicitante</span>
            )}
            <ChevronDown size={16} color={comum.colors.interfaceText300} />
          </SolicitanteSelectButton>
        </FieldContainer>
      )}

      {isModalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Selecionar Escritório Solicitante</h3>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>

            <SearchContainer>
              <input
                type="text"
                placeholder="Buscar por nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </SearchContainer>

            <OfficeList>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--c718096)' }}>
                  Carregando escritórios...
                </div>
              ) : filteredEscritorios.length > 0 ? (
                filteredEscritorios.map((escritorio) => (
                  <OfficeItem
                    key={escritorio.id_escritorio_solicitante}
                    isSelected={selectedEscritorio?.id_escritorio_solicitante === escritorio.id_escritorio_solicitante}
                    onClick={() => handleSelectEscritorio(escritorio)}
                  >
                    <div className="office-info">
                      <div className="office-name">{escritorio.nome}</div>
                      <div className="office-codes">
                        <span className="code-label">Código:</span>
                        <span className="code-new">ES{escritorio.id_escritorio_solicitante}</span>
                        {escritorio.codigo && (
                          <>
                            <span className="code-separator">•</span>
                            <span className="code-old">{escritorio.codigo}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </OfficeItem>
                ))
              ) : (
                <NoResultsMessage>
                  Nenhum escritório encontrado para "{searchTerm}"
                </NoResultsMessage>
              )}
            </OfficeList>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
});

const InformacoesBasicas: React.FC<InformacoesBasicasProps> = ({
  tipoDemanda,
  tituloFormulario,
  erros,
  setDadosBasicos,
  dadosBasicos,
  setComarca
}) => {
  // Estado unificado - removendo estado duplicado
  const [dados, setDados] = useState<IDadosBasicosNovaDemanda>(dadosBasicos);
  const { user } = useAuth();
  

  // Opções para os campos de seleção - memoizadas
  const opcoesNatureza = useMemo(() => [
    { value: 'Extrajudicial', label: 'Extrajudicial' },
    { value: 'Judicial', label: 'Judicial' },
    { value: 'Administrativo', label: 'Administrativo' }
  ], []);

  // Opções para fuso horário
  const opcoesFusoHorario = useMemo(() => [
    { value: 'FNT', label: 'FNT (UTC-2) - Fernando de Noronha', displayValue: 'FNT' },
    { value: 'BRT', label: 'BRT (UTC-3) - Brasília', displayValue: 'BRT' },
    { value: 'AMT', label: 'AMT (UTC-4) - Manaus', displayValue: 'AMT' },
    { value: 'ACT', label: 'ACT (UTC-5) - Rio Branco', displayValue: 'ACT' }
  ], []);

  // Componente customizado para exibir apenas a sigla no campo selecionado
  const SingleValue = ({ children, ...props }: any) => {
    const selectedOption = opcoesFusoHorario.find(option => option.value === props.getValue()[0]?.value);
    return (
      <components.SingleValue {...props}>
        {selectedOption?.displayValue || children}
      </components.SingleValue>
    );
  };

  // Verifica se é solicitante baseado no tipo de demanda
  const isSolicitante = useMemo(() => tipoDemanda === 'solicitante', [tipoDemanda]);

  // Sincroniza estado interno quando dadosBasicos mudar (ex: duplicação)
  useEffect(() => {
    setDados(dadosBasicos);
  }, [dadosBasicos]);

  // Atualiza os dados no componente pai - otimizado
  useEffect(() => {
    setDadosBasicos(dados);
  }, [dados, setDadosBasicos]);

  // Funções otimizadas com useCallback
  const handleInputChange = useCallback((campo: keyof IDadosBasicosNovaDemanda, valor: any) => {
    setDados(prev => {
      const newData = { ...prev, [campo]: valor };
      
      // Se a natureza mudou e há um número de processo, limpa o número
      if (campo === 'natureza' && prev.natureza !== valor && prev.numero_processo) {
        newData.numero_processo = '';
      }
      
      return newData;
    });
  }, []);

  const handleInputChangeWrapper = useCallback((campo: string, valor: any) => {
    handleInputChange(campo as keyof IDadosBasicosNovaDemanda, valor);
  }, [handleInputChange]);

  // Função para aplicar fuso quando comarca é selecionada
  const handleComarcaSelected = useCallback((estadoSelecionado: string) => {
    const fusoDetectado = getFusoHorarioPorEstado(estadoSelecionado);
    handleInputChange('data_tz', fusoDetectado);
  }, [handleInputChange]);

  const handleEstadoChange = useCallback((valor: string) => {
    setDados(prev => ({ ...prev, estado: valor, id_comarca: '' }));
    setComarca('');
  }, [setComarca]);

  const handleDateTimeChange = useCallback((dataString: string, horaString: string) => {
    if (dataString && horaString && horaString !== '--:--') {
      const [year, month, day] = dataString.split('-').map(Number);
      const [hours, minutes] = horaString.split(':').map(Number);
      
      if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const dataFatal = new Date(year, month - 1, day, hours, minutes);
        handleInputChange('data', dataFatal);
      }
    } else if (!dataString) {
      handleInputChange('data', undefined);
    }
  }, [handleInputChange]);


  const formatCurrencyDisplay = useCallback((valor: number | undefined): string => {
    return formatCurrency(valor);
  }, []);

  const parseCurrencyInput = useCallback((inputValue: string): number => {
    // Remove tudo exceto números
    const numbersOnly = inputValue.replace(/\D/g, '');
    
    // Se não há números, retorna 0
    if (!numbersOnly) return 0;
    
    // Converte para centavos (divide por 100)
    return parseInt(numbersOnly) / 100;
  }, []);

  const formatCurrencyInput = useCallback((inputValue: string): string => {
    // Remove tudo exceto números
    const numbersOnly = inputValue.replace(/\D/g, '');
    
    // Se não há números, retorna R$ 0,00
    if (!numbersOnly) return 'R$ 0,00';
    
    // Converte para centavos e formata
    const valueInCents = parseInt(numbersOnly) / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valueInCents);
  }, []);

  const handleCurrencyChange = useCallback((campo: 'valor_cliente' | 'valor_profissional', inputValue: string) => {
    const numericValue = parseCurrencyInput(inputValue);
    handleInputChange(campo, numericValue);
  }, [parseCurrencyInput, handleInputChange]);

  // Função para aplicar máscara do número do processo - simplificada
  const applyProcessNumberMask = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 7) return numbers;
    if (numbers.length <= 9) return `${numbers.slice(0, 7)}-${numbers.slice(7)}`;
    if (numbers.length <= 13) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9)}`;
    if (numbers.length <= 14) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13)}`;
    if (numbers.length <= 16) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13, 14)}.${numbers.slice(14)}`;
    return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13, 14)}.${numbers.slice(14, 16)}.${numbers.slice(16, 20)}`;
  }, []);

  // Função para lidar com mudança no número do processo - otimizada
  const handleProcessNumberChange = useCallback((value: string) => {
    let processedValue = value;
    
    if (dados.natureza === 'Judicial') {
      processedValue = value.replace(/\D/g, '');
      if (processedValue.length > 20) {
        processedValue = processedValue.slice(0, 20);
      }
      processedValue = applyProcessNumberMask(processedValue);
    } else {
      processedValue = value.replace(/\D/g, '');
      if (processedValue.length > 20) {
        processedValue = processedValue.slice(0, 20);
      }
    }
    
    handleInputChange('numero_processo', processedValue);
  }, [dados.natureza, applyProcessNumberMask, handleInputChange]);

  // Funções para obter título e placeholder - memoizadas
  const processNumberTitle = useMemo(() => {
    return dados.natureza === 'Judicial' ? 'Número do processo' : 'Número para identificação';
  }, [dados.natureza]);

  const processNumberPlaceholder = useMemo(() => {
    return dados.natureza === 'Judicial' ? '0000000-00.0000.0.00.0000' : 'Digite o número para identificação';
  }, [dados.natureza]);

  const handleTipoParteChange = useCallback((tipo: 'cliente' | 'solicitante' | 'nenhum') => {
    setDados(prev => {
      const newData = { ...prev, tipo_parte: tipo };
      
      if (tipo === 'cliente') {
        delete newData.id_escritorio_solicitante;
      } else if (tipo === 'solicitante') {
        newData.nome_cliente = '';
        newData.codigo_cliente = '';
      } else {
        delete newData.id_escritorio_solicitante;
        newData.nome_cliente = '';
        newData.codigo_cliente = '';
      }
      
      return newData;
    });
  }, []);

  return (
    <Container>
      <FormSectionTitle>{tituloFormulario}</FormSectionTitle>

      <FormGrid>
        {/* Seletor de Tipo de Parte - Cliente ou Solicitante */}


        {/* Linha 1: Estado e Comarca */}
        <FieldContainer>
          <EstadoSelect
            value={dados.estado || ''}
            onChange={handleEstadoChange}
            error={!!erros?.uf}
          />
          {erros?.uf && <ErrorMessage>{erros.uf}</ErrorMessage>}
        </FieldContainer>

        <FieldContainer>
          <ComarcaSelect
            estado={dados.estado || ''}
            value={dados.id_comarca || ''}
            onChange={(valor) => handleInputChange('id_comarca', valor)}
            error={!!erros?.id_comarca}
            setComarca={setComarca}
            onComarcaSelected={handleComarcaSelected}
            handleInputChange={handleInputChangeWrapper}
          />
          {erros?.id_comarca && <ErrorMessage>{erros.id_comarca}</ErrorMessage>}
        </FieldContainer>

        {/* Linha 2: Natureza e Número do Processo */}
        <FieldContainer>
          <Label required>Natureza</Label>
          <Select
            name="natureza"
            styles={createSelectStyles(!!(erros?.natureza))}
            components={selectComponents}
            theme={selectTheme}
            placeholder="Selecione a natureza"
            value={opcoesNatureza.find(option => option.value === dados.natureza)}
            onChange={(selected: any) => handleInputChange('natureza', selected?.value || '')}
            options={opcoesNatureza}
          />
          {erros?.natureza && <ErrorMessage>{erros.natureza}</ErrorMessage>}
        </FieldContainer>

        <FieldContainer>
          <Label required>{processNumberTitle}</Label>
          <Input
            type="text"
            placeholder={processNumberPlaceholder}
            value={dados.numero_processo}
            onChange={(e) => handleProcessNumberChange(e.target.value)}
            hasError={!!erros?.numero_processo}
            maxLength={dados.natureza === 'Judicial' ? 25 : 20}
          />
          {erros?.numero_processo && <ErrorMessage>{erros.numero_processo}</ErrorMessage>}
        </FieldContainer>

        {/* Linha 3: Valor do Cliente, Valor do Serviço e Prazo Fatal */}
        <ThreeColumnRow>
          {
            user.profile !== 'Solicitante' &&
            <>
          <FieldContainer>
            <Label required>Valor do Cliente</Label>
            <Input
              type="text"
              placeholder="R$ 0,00"
              value={formatCurrencyDisplay(dados.valor_cliente)}
              onChange={(e) => handleCurrencyChange('valor_cliente', e.target.value)}
              hasError={!!erros?.valor_cliente}
            />
            {erros?.valor_cliente && <ErrorMessage>{erros.valor_cliente}</ErrorMessage>}
          </FieldContainer>

          <FieldContainer>
            <Label required>Valor do Serviço</Label>
            <Input
              type="text"
              placeholder="R$ 0,00"
              value={formatCurrencyDisplay(dados.valor_profissional)}
              onChange={(e) => handleCurrencyChange('valor_profissional', e.target.value)}
              hasError={!!erros?.valor_profissional}
            />
            {erros?.valor_profissional && <ErrorMessage>{erros.valor_profissional}</ErrorMessage>}
          </FieldContainer>
            </>
          }

          <FieldContainer>
            <Label required>Prazo Fatal</Label>
            <DateTimeContainer>
              <DatePickerContainer>
                <DateTimePicker
                  value={dados.data ? `${dados.data.getFullYear()}-${String(dados.data.getMonth() + 1).padStart(2, '0')}-${String(dados.data.getDate()).padStart(2, '0')} ${String(dados.data.getHours()).padStart(2, '0')}:${String(dados.data.getMinutes()).padStart(2, '0')}` : ''}
                  onChange={handleDateTimeChange}
                  placeholder="Selecione a data e hora fatal"
                  error={!!erros?.data}
                />
              </DatePickerContainer>
              <TimezoneFieldContainer>
              <Select
                name="fuso_horario"
                styles={createTimezoneSelectStyles(!!erros?.data)}
                components={{
                  ...selectComponents,
                  SingleValue: SingleValue
                }}
                theme={selectTheme}
                placeholder="Fuso"
                value={opcoesFusoHorario.find(option => option.value === dados.data_tz)}
                onChange={(selected: any) => handleInputChange('data_tz', selected?.value || 'BRT')}
                options={opcoesFusoHorario}
                isSearchable={false}
                isClearable={false}
              />
              </TimezoneFieldContainer>
            </DateTimeContainer>
            {erros?.data && <ErrorMessage>{erros.data}</ErrorMessage>}
          </FieldContainer>
        </ThreeColumnRow>

        {/* Seletor de Tipo de Parte - Cliente ou Solicitante */}
        {user.profile !== 'Solicitante' && (
          <VincularAContainer>
            <VincularALabelAndTabWrapper>
              <VincularALabel>VINCULAR A</VincularALabel>
              <TipoParteContainer>
                <TipoParteButton
                  type="button"
                  selected={dados.tipo_parte === 'nenhum'}
                  onClick={() => handleTipoParteChange('nenhum')}
                >
                  <XCircle />
                  Nenhum
                </TipoParteButton>
                <TipoParteButton
                  type="button"
                  selected={dados.tipo_parte === 'solicitante'}
                  onClick={() => handleTipoParteChange('solicitante')}
                >
                  <Building2 />
                  Solicitante
                </TipoParteButton>
                <TipoParteButton
                  type="button"
                  selected={dados.tipo_parte === 'cliente'}
                  onClick={() => handleTipoParteChange('cliente')}
                >
                  <User />
                  Cliente
                </TipoParteButton>
              </TipoParteContainer>
            </VincularALabelAndTabWrapper>
            
            <VincularAFormControl>
              {dados.tipo_parte === 'solicitante' && (
                <VincularASolicitanteWrapper>
                  <Label required>Solicitante</Label>
                  <SolicitanteSelector
                    value={dados.id_escritorio_solicitante}
                    onChange={(id) => handleInputChange('id_escritorio_solicitante', id)}
                    error={!!erros?.id_escritorio_solicitante}
                    inline={true}
                    required={true}
                    errorMessage={erros?.id_escritorio_solicitante}
                  />
                </VincularASolicitanteWrapper>
              )}
              
              {dados.tipo_parte === 'cliente' && (
                <>
                  <Label required style={{gridColumn: 1, gridRow: 1}}>Nome</Label>
                  <Label required style={{gridColumn: 2, gridRow: 1}}>Código do Cliente</Label>
                  <Input
                    type="text"
                    placeholder="Digite o nome"
                    value={dados.nome_cliente || ''}
                    onChange={(e) => handleInputChange('nome_cliente', e.target.value)}
                    hasError={!!erros?.nome_cliente}
                    style={{gridColumn: 1, gridRow: 2}}
                  />
                  <Input
                    type="text"
                    placeholder="Digite o código do cliente"
                    value={dados.codigo_cliente || ''}
                    onChange={(e) => handleInputChange('codigo_cliente', e.target.value)}
                    hasError={!!erros?.codigo_cliente}
                    style={{gridColumn: 2, gridRow: 2}}
                  />
                  <ErrorMessage style={{gridColumn: 1, gridRow: 3}}>{erros?.nome_cliente || ''}</ErrorMessage>
                  <ErrorMessage style={{gridColumn: 2, gridRow: 3}}>{erros?.codigo_cliente || ''}</ErrorMessage>
                </>
              )}
            </VincularAFormControl>
          </VincularAContainer>
        )}
      </FormGrid>
    </Container>
  );
};

export default InformacoesBasicas;
export type {IDadosBasicosNovaDemanda, InformacoesBasicasProps} from '../types';

